import { AccessList, useHasAccess, useNavigation, useProvider } from '~/Hooks';
import { useRequest } from '~/Hooks/useRequest';
import { CORE_SERVICE_URLS, EVENT_SERVICE_URLS } from '~/Constants/Urls.constants';
import { ExtendedEventGroup } from '../ExtendedEventGroup.model';
import { IErrorResponse } from '~/Models/DTO/Error.models';
import { requestSendZoomSessionData, selectZoomIntegration } from '~/store/zoom/integration';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { ZoomSessionType } from '~/Models/DTO/Zoom.model';
import { useEventGroupRequestParams } from './useEventGroupRequestParams';
import { omit } from 'lodash-es';
import { selectCurrentTour, setProductTour } from '~/store/app/productTours';
import { Tour } from '~/Models/interfaces/Tour.enum';
import { Route } from '~/Constants/Routes.constants';

export const useCreateNewEventGroupRequest = () => {
  const dispatch = useDispatch();
  const currentTour = useSelector(selectCurrentTour);
  const setTour = bindActionCreators(setProductTour, dispatch);

  const { requestWithCallback } = useRequest();
  const { currentProvider } = useProvider();
  const { goBack, navigate } = useNavigation();
  const zoomIntegration = useSelector(selectZoomIntegration);
  const createZoomSession = bindActionCreators(requestSendZoomSessionData, dispatch);
  const getRequestParams = useEventGroupRequestParams();
  const { hasAccess } = useHasAccess();

  return {
    createNewEventGroup: async ({
      eventGroupToSend,
      setSubmissionError,
    }: {
      eventGroupToSend: ExtendedEventGroup;
      setSubmissionError: (error: IErrorResponse) => void;
    }) => {
      const {
        hasAccessToZoom,
        isZoomSelected,
        isZoomWebinar,
        timeSeries,
        isUSCIntegrationEnabled,
        ...restEventGroupData
      } = omit(eventGroupToSend, [
        'publicCancellationRefundEndAsNumber',
        'publicCancellationRefundEndType',
        'publicBookingWindowStartType',
        'publicBookingWindowEndType',
      ]);

      const shouldSyncEventGroup =
        hasAccess(AccessList.FORCE_EVENT_GROUP_USC_SYNC) || isUSCIntegrationEnabled;
      await requestWithCallback<Required<ExtendedEventGroup>>(
        getRequestParams({
          eventGroups: restEventGroupData,
          shouldSyncEventGroup: hasAccess(AccessList.FORCE_EVENT_GROUP_USC_SYNC),
        }),
        async ({ id: eventGroupId }) => {
          await Promise.allSettled?.(
            (timeSeries || []).map((timeSeries) =>
              requestWithCallback({
                url: EVENT_SERVICE_URLS.EVENT_SERIES,
                method: 'POST',
                data: { ...timeSeries, eventGroupId },
              }),
            ),
          );
          if (currentProvider && shouldSyncEventGroup) {
            await requestWithCallback({
              url: CORE_SERVICE_URLS.SYNCED_EVENT_GROUPS,
              method: 'POST',
              data: { providerId: currentProvider.uuid, eventGroupId },
            });
          }

          if (hasAccessToZoom && isZoomSelected) {
            createZoomSession({
              eventGroupId,
              type: isZoomWebinar ? ZoomSessionType.WEBINAR : ZoomSessionType.MEETING,
              zoomIntegrationId: zoomIntegration.id,
            });
          }
          if (currentTour === Tour.EVENT_CREATION_PENDING) {
            setTour(Tour.EVENT_CREATION_DONE);
            navigate(Route.EVENTGROUPS);
          } else {
            goBack();
          }
        },
        (error) => setSubmissionError(error),
      );
    },
  };
};
